import { useState, useEffect } from 'react';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import './RSVPSection.css';

export default function RSVPSection({ partyId, user, userProfile, isHost, guestLimit, isGuestView }) {
    const [status, setStatus] = useState(null);
    const [plusGuests, setPlusGuests] = useState(0);
    const [isEditing, setIsEditing] = useState(false);
    const [isLoading, setIsLoading] = useState(!isGuestView);

    const [tempStatus, setTempStatus] = useState(null);
    const [tempPlusGuests, setTempPlusGuests] = useState(0);

    useEffect(() => {
        if (!user || !partyId || isGuestView) {
            if (isGuestView) {
                setStatus(null);
                setPlusGuests(0);
                setTempStatus(null);
                setTempPlusGuests(0);
                setIsLoading(false);
            }
            return;
        }

        const fetchRSVP = async () => {
            setIsLoading(true);
            try {
                const rsvpRef = doc(db, 'rsvps', `${partyId}_${user.uid}`);
                const snap = await getDoc(rsvpRef);

                if (snap.exists()) {
                    const data = snap.data();
                    setStatus(data.status);
                    setPlusGuests(data.plusGuests || 0);
                    setTempStatus(data.status);
                    setTempPlusGuests(data.plusGuests || 0);
                } else if (isHost) {
                    await handleSave('Yes', 0);
                }
            } catch (err) {
                console.error("Error fetching RSVP:", err);
            } finally {
                setIsLoading(false);
            }
        };

        fetchRSVP();
    }, [partyId, user, isHost, isGuestView]);

    const handleSave = async (newStatus = tempStatus, guestsCount = tempPlusGuests) => {
        if (!newStatus) return;

        if (isGuestView) {
            // Simulation only
            setStatus(newStatus);
            setPlusGuests(guestsCount);
            setIsEditing(false);
            return;
        }

        try {
            const rsvpRef = doc(db, 'rsvps', `${partyId}_${user.uid}`);
            await setDoc(rsvpRef, {
                partyId,
                userId: user.uid,
                name: userProfile?.name || 'Guest',
                status: newStatus,
                plusGuests: guestsCount,
                updatedAt: serverTimestamp()
            });
            setStatus(newStatus);
            setPlusGuests(guestsCount);
            setIsEditing(false);
        } catch (err) {
            console.error("Error saving RSVP:", err);
            alert("Failed to save RSVP");
        }
    };

    const handleStartEditing = () => {
        setTempStatus(status);
        setTempPlusGuests(plusGuests);
        setIsEditing(true);
    };

    const handleCancel = () => {
        setTempStatus(status);
        setTempPlusGuests(plusGuests);
        setIsEditing(false);
    };

    if (isLoading) return <div className="rsvp-loading">Loading RSVP...</div>;

    // The host is always attending, show a special message
    if (isHost) {
        return (
            <div className="rsvp-container host-mode">
                <div className="rsvp-status-display">
                    <span className="rsvp-text">
                        👑 <strong>You're the host!</strong> See you there! 🎉
                    </span>
                </div>
            </div>
        );
    }

    const showForm = !status || isEditing;

    return (
        <div className={`rsvp-container ${!status ? 'unfilled' : ''}`}>
            {showForm ? (
                <div className="rsvp-form">
                    <h3>Will you be attending?</h3>
                    <div className="rsvp-options">
                        <button
                            className={`rsvp-btn yes ${tempStatus === 'Yes' ? 'selected' : ''}`}
                            onClick={() => setTempStatus('Yes')}
                        >
                            Yes
                        </button>
                        <button
                            className={`rsvp-btn maybe ${tempStatus === 'Maybe' ? 'selected' : ''}`}
                            onClick={() => setTempStatus('Maybe')}
                        >
                            Maybe
                        </button>
                        <button
                            className={`rsvp-btn no ${tempStatus === 'No' ? 'selected' : ''}`}
                            onClick={() => setTempStatus('No')}
                        >
                            No
                        </button>
                    </div>

                    {(tempStatus === 'Yes' || tempStatus === 'Maybe' || !tempStatus) && guestLimit !== -1 && (
                        <div className="guest-selector-container">
                            <label>Bringing anyone else?</label>
                            <div className="guest-selector">
                                <button
                                    className={`guest-count-btn ${tempPlusGuests === 0 ? 'active' : ''}`}
                                    onClick={() => setTempPlusGuests(0)}
                                >
                                    Just me
                                </button>

                                {(guestLimit >= 1 || guestLimit === 0) && (
                                    <button
                                        className={`guest-count-btn ${tempPlusGuests === 1 ? 'active' : ''}`}
                                        onClick={() => setTempPlusGuests(1)}
                                    >
                                        +1
                                    </button>
                                )}

                                {(guestLimit === 2 || guestLimit === 0) && (
                                    <button
                                        className={`guest-count-btn ${tempPlusGuests === 2 ? 'active' : ''}`}
                                        onClick={() => setTempPlusGuests(2)}
                                    >
                                        +2
                                    </button>
                                )}

                                {guestLimit === 0 && (
                                    <div className={`plus-input-wrapper ${tempPlusGuests >= 3 ? 'active' : ''}`}>
                                        <span className="plus-prefix">+</span>
                                        <input
                                            type="number"
                                            min="3"
                                            max="9"
                                            placeholder="3-9"
                                            value={tempPlusGuests >= 3 ? tempPlusGuests : ''}
                                            onChange={(e) => {
                                                const val = Number(e.target.value);
                                                if (val >= 3 && val <= 9) setTempPlusGuests(val);
                                                else if (e.target.value === '') setTempPlusGuests(0);
                                            }}
                                            className="plus-blank-input"
                                        />
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    <div className="form-actions">
                        <button
                            className="rsvp-confirm-btn"
                            disabled={!tempStatus}
                            onClick={() => handleSave()}
                        >
                            Confirm RSVP
                        </button>
                        {(status || isEditing) && (
                            <button className="rsvp-cancel-btn" onClick={handleCancel}>
                                Cancel
                            </button>
                        )}
                    </div>
                </div>
            ) : (
                <div className="rsvp-status-display">
                    <span className="rsvp-text">
                        Your RSVP: <strong className={`status-${status.toLowerCase()}`}>{status}</strong>
                        {plusGuests > 0 && <span className="plus-badge">+{plusGuests}</span>}
                    </span>
                    <button className="rsvp-edit-btn" onClick={handleStartEditing}>
                        Edit
                    </button>
                </div>
            )}
        </div>
    );
}
